import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import { useTranslation } from "react-i18next";
import PiChipSelect, { PiChipSelectOption } from "../../../components/PiChipSelect";
import { emotionBubbleStylesMapped } from "../../../models/interactions/EmotionBubble";
import { InteractionType } from "../../../models/interactions/Interaction";
import { messageBubbleStyles, noMessageBubbleAnimation } from "../../../models/interactions/MessageBubble";
import PiAnimationPicker from "../animation/PiAnimationPicker";
import PiEmotionGrid from "../components/PiEmotionGrid";
import PiActorChipSelect from "../components/PiActorChipSelect";
import PiLookModeChipSelect from "../components/PiLookModeChipSelect";
import PiActorPropChipSelect from "../components/PiActorPropChipSelect";
import { PiSettingsGap, PiSettingsList, PiSettingsRow } from "../components/PiSettingsList";
import { useConversationCast, useConversationLines } from "./ConversationLines.hook";
import { hasLookatTarget, isNoLookat } from "../../../models/NoLookat";

/**
 * Settings for one conversation line, shown beside the chat (Design Studio
 * `screens/lookat-rework/cv2b-chat-panel.html`). Same rows as the Message Bubble
 * popup; the Look and Timing rows are left out until the game side supports them.
 */
export default function ConversationLineSettings(props: { lineId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { conversation, updateLine } = useConversationLines();
  const { bubbleProps, nameOf, isProp } = useConversationCast(conversation.id);

  const index = conversation.messages.findIndex((m) => m.id === props.lineId);
  const line = conversation.messages[index];
  if (!line) return null;

  const isEmotion = line.type === InteractionType.EmotionBubble;
  const speakerIsProp = isProp(line.npcId);

  // Same rules as the Message Bubble popup: a prop can't look at anyone, and an
  // actor can't look at itself.
  const changeSpeaker = (id: string) =>
    updateLine(
      line.id,
      isProp(id)
        ? // a prop can't look at anyone or play an animation
          { npcId: id, lookAt: "", animation: { ...noMessageBubbleAnimation } }
        : id === line.lookAt
        ? { npcId: id, lookAt: "" }
        : { npcId: id }
    );

  const styleOptions: PiChipSelectOption[] = messageBubbleStyles.map((style) => ({
        id: style,
        label: style,
    icon: <ChatBubbleOutlineOutlinedIcon className="flex-none" sx={{ fontSize: 18 }} />,
  }));
  const emotionName =
    emotionBubbleStylesMapped.find((e) => e.name === line.messageBubbleStyle)?.displayName ??
    line.messageBubbleStyle;

  const title = [
    String(t("conversation_ui.line", { number: index + 1 })),
    [
      nameOf(line.npcId),
      isNoLookat(line.lookAt)
        ? String(t("chip_select.no_actor"))
        : line.lookAt
          ? nameOf(line.lookAt)
          : "",
    ].filter(Boolean).join(" → "),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <PiSettingsList className="min-h-0 max-h-full overflow-y-auto pit-scrollbar">
      <div
        className="flex items-center gap-2 px-4 py-3 border-b border-solid border-x-0 border-t-0 border-interaction-primary-washed"
        data-testid="conversation-line-settings"
      >
        <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-interaction-primary">
          {title}
        </span>
        <button
          type="button"
          onClick={props.onClose}
          aria-label={String(t("conversation_ui.close_settings"))}
          className="flex p-1 rounded-[6px] border-none bg-transparent cursor-pointer text-interaction-primary-faded hover:text-interaction-primary"
          data-testid="conversation-line-settings-close"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      <PiSettingsRow label={String(t("bubble_ui.speaker"))}>
        <PiActorPropChipSelect
          aria-label={String(t("bubble_ui.speaker"))}
          value={line.npcId}
          onChange={changeSpeaker}
          availableProps={bubbleProps}
        />
      </PiSettingsRow>
      <PiSettingsRow label={String(t("bubble_ui.talks_to"))} disabled={speakerIsProp}>
        <PiActorChipSelect
          aria-label={String(t("bubble_ui.talks_to"))}
          value={speakerIsProp ? "" : line.lookAt}
          onChange={(id) => updateLine(line.id, { lookAt: id })}
          excludeActors={[line.npcId]}
          allowKeepCurrent
          allowNoActor
          disabled={speakerIsProp}
        />
      </PiSettingsRow>
      <PiSettingsRow label={String(t("look_mode.label"))} disabled={speakerIsProp || !hasLookatTarget(line.lookAt)}>
        <PiLookModeChipSelect
          value={line.finalIK}
          onChange={(finalIK) => updateLine(line.id, { finalIK })}
          disabled={speakerIsProp || !hasLookatTarget(line.lookAt)}
        />
      </PiSettingsRow>

      <PiSettingsGap />
      <PiSettingsRow
        label={String(t("bubble_ui.animation"))}
        below={
          <PiAnimationPicker
            disabled={speakerIsProp}
            selectedAnimation={line.animation}
            onChange={(animation) => updateLine(line.id, { animation })}
          />
        }
      />
      {isEmotion ? (
        // Same emoji grid as the Emotion Bubble popup (Design Studio e3-emotion).
        <PiSettingsRow
          label={String(t("conversation_ui.emotion"))}
          below={
            <PiEmotionGrid
              value={line.messageBubbleStyle}
              onChange={(emotion) => updateLine(line.id, { messageBubbleStyle: emotion })}
            />
          }
        >
          <span className="text-[13px] font-semibold text-interaction-primary">{emotionName}</span>
        </PiSettingsRow>
      ) : (
        <PiSettingsRow label={String(t("bubble_ui.bubble_style"))}>
          <PiChipSelect
            aria-label={String(t("bubble_ui.bubble_style"))}
            tone="setting"
            value={line.messageBubbleStyle}
            options={styleOptions}
            onChange={(style) => updateLine(line.id, { messageBubbleStyle: style })}
            placeholder={styleOptions[0]?.label ?? ""}
          />
        </PiSettingsRow>
      )}
    </PiSettingsList>
  );
}
